import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2, Flame } from "lucide-react";
import { fetchCiphers, searchByNumber } from "@/lib/api";
import { recordSearch, getTrending } from "@/lib/history";
import FeaturedGrid from "@/components/search/FeaturedGrid";
import ResultsView from "@/components/search/ResultsView";
import { toast } from "sonner";

const SUGGESTED = [33, 44, 45, 55, 66, 83, 87, 93, 111, 317, 333, 666];

export default function NumberSearch({ initialValue }) {
  const [value, setValue] = useState(initialValue || "");
  const [ciphers, setCiphers] = useState([]);
  const [selected, setSelected] = useState([]); // empty = all
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    fetchCiphers().then(setCiphers).catch(() => toast.error("Failed to load ciphers"));
    setTrending(getTrending());
  }, []);

  const toggleCipher = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const runSearch = useCallback(async (val) => {
    const target = parseInt(val ?? value, 10);
    if (Number.isNaN(target)) {
      toast.error("Enter a number");
      return;
    }
    setLoading(true);
    try {
      const cipherCsv = selected.length > 0 ? selected.join(",") : undefined;
      const data = await searchByNumber(target, cipherCsv);
      setResults(data);
      recordSearch(target);
      setTrending(getTrending());
      if (data.total === 0) toast(`No phrases found = ${target}`);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }, [value, selected]);

  // Auto-run the URL-provided search exactly once. The ref guard keeps the
  // effect from re-firing when runSearch's identity changes on later renders.
  const bootedRef = useRef(false);
  useEffect(() => {
    if (initialValue && !bootedRef.current) {
      bootedRef.current = true;
      runSearch(initialValue);
    }
  }, [initialValue, runSearch]);

  const pickNumber = (n) => {
    setValue(String(n));
    runSearch(String(n));
  };

  const cipherLabel = useMemo(() => {
    const labels = {};
    ciphers.forEach((c) => (labels[c.key] = c.label));
    return labels;
  }, [ciphers]);

  return (
    <section className="space-y-10">
      {/* HERO */}
      <div className="rise">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
          // The Decoding Console
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          Type a number. <span className="gold italic">Find the message.</span>
        </h1>
        <p className="muted mt-5 max-w-2xl leading-relaxed">
          Search a curated dictionary of conspiratorial phrases across {ciphers.length || "19"} ciphers from
          gematrinator. Phrases ranked by cipher-match count and category weight — high-rating
          phrases bubble to the top.
        </p>
      </div>

      {/* SEARCH BAR */}
      <div className="rise rise-1 panel p-6 md:p-8">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
          Target Number
        </label>
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="number-search-input"
            type="number"
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="e.g. 66, 317, 333…"
            className="terminal-input flex-1 w-full min-w-0 px-4 py-4 text-2xl"
          />
          <button
            data-testid="number-search-submit"
            onClick={() => runSearch()}
            disabled={loading}
            className="btn-primary px-6 py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60 sm:shrink-0"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
            Decode
          </button>
        </div>

        {trending.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2 items-center" data-testid="trending-row">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] gold mr-2 flex items-center gap-1.5">
              <Flame size={12} /> Trending:
            </span>
            {trending.map((t) => (
              <button
                key={t.value}
                data-testid={`trending-${t.value}`}
                onClick={() => pickNumber(t.value)}
                className="font-mono text-xs px-3 py-1 border border-[rgba(212,175,55,0.45)] text-[var(--primary)] hover:bg-[rgba(212,175,55,0.1)] transition-colors flex items-center gap-1.5"
                title={`Searched ${t.count} time${t.count === 1 ? "" : "s"} this week`}
              >
                {t.value}
                <span className="text-[9px] muted">×{t.count}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] muted mr-2">
            Try:
          </span>
          {SUGGESTED.map((n) => (
            <button
              key={n}
              data-testid={`suggest-${n}`}
              onClick={() => pickNumber(n)}
              className="font-mono text-xs px-3 py-1 border border-white/10 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
            >
              {n}
            </button>
          ))}
          <button
            data-testid="toggle-filters"
            onClick={() => setShowFilters((v) => !v)}
            className="ml-auto font-mono text-[11px] muted hover:text-white tracking-wider"
          >
            {showFilters ? "− Hide filters" : "+ Filter ciphers"} ({selected.length || "all"})
          </button>
        </div>

        {showFilters && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="flex flex-wrap gap-2">
              {ciphers.map((c) => (
                <button
                  key={c.key}
                  onClick={() => toggleCipher(c.key)}
                  data-testid={`cipher-toggle-${c.key}`}
                  className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                    selected.length === 0 || selected.includes(c.key)
                      ? "border-[var(--primary)] text-[var(--primary)] bg-[rgba(212,175,55,0.08)]"
                      : "border-white/10 muted hover:text-white"
                  }`}
                >
                  {c.label}
                </button>
              ))}
              {selected.length > 0 && (
                <button
                  onClick={() => setSelected([])}
                  className="text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 muted hover:text-white"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* FEATURED DECODES — only when no active search */}
      {!results && <FeaturedGrid onPick={pickNumber} />}

      {/* RESULTS */}
      {results && (
        <ResultsView results={results} cipherLabel={cipherLabel} onResearch={runSearch} />
      )}
    </section>
  );
}
